import { MonitoringPopup } from "src/components/Homes/HomeDetails/MonitoringPopup"
import { MotionAutomationToggleIcon } from "src/components/Homes/HomeDetails/MotionAutomation/MotionAutomationToggleIcon"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import { usePatchHomeMotionAutomation } from "src/data/organizations/queries/homeQueries"
import { useTranslate } from "src/i18n/useTranslate"

/**
 * MotionAutomationPopover
 * Placeholder popover for future Motion Automation settings.
 * Integrates with backend using usePatchHomeMotionAutomation.
 * @example <MotionAutomationPopover open anchorEl={el} onClose={() => {}} homeId="ronSwansonHome" />
 */
export function MotionAutomationPopover({
  open,
  anchorEl,
  onClose,
  state = "disabled",
  homeId,
}: {
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  state: "enabled" | "disabled" | undefined
  homeId: string
}) {
  const { t, langKeys } = useTranslate()
  const { org } = useOrganization()
  const patchHomeMotionAutomation = usePatchHomeMotionAutomation({
    orgId: org.id,
  })

  const enabled = state === "enabled"

  /**
   * Calls backend to disable motion automation.
   */
  function handleTurnOffClick() {
    patchHomeMotionAutomation.mutate(
      {
        path: { home_id: homeId },
        body: { mode: "disabled" },
      },
      { onSuccess: onClose }
    )
  }

  /**
   * Calls backend to enable motion automation.
   */
  function handleTurnOnClick() {
    patchHomeMotionAutomation.mutate(
      {
        path: { home_id: homeId },
        body: { mode: "enabled" },
      },
      { onSuccess: onClose }
    )
  }

  const buttons = [
    {
      icon: <MotionAutomationToggleIcon toggleState="off" state={state} />,
      onClick: () => {
        handleTurnOffClick()
      },
      label: t(langKeys.off),
      id: "off",
      disablePointerEvents: !enabled,
    },
    {
      icon: <MotionAutomationToggleIcon toggleState="on" state={state} />,
      onClick: () => {
        handleTurnOnClick()
      },
      label: t(langKeys.on),
      disableOnWarning: true,
      id: "on",
      disablePointerEvents: enabled,
    },
  ]

  return (
    <MonitoringPopup
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title={t(langKeys.motion_automation_title)}
      description={t(langKeys.motion_automation_description)}
      buttons={buttons}
    ></MonitoringPopup>
  )
}
